// Prompt assembly. The base instructions and per-persona system prompt form
// the cached block; story context and user library form the dynamic block.

import type { LibraryWord, Persona, Story } from '../types';

export const BASE_INSTRUCTIONS = `RESPONSE FORMAT — CRITICAL:
You MUST respond with ONLY valid JSON, no markdown, no commentary, no leading prose.
Schema:
{
  "hz": string,                 // Your reply in Mandarin Chinese characters.
  "py": string,                 // Pinyin with tone marks, space-separated.
  "en": string,                 // Natural-register English translation.
  "words": [                    // 0–4 words from your reply worth saving.
    { "hz": string, "py": string, "en": string }
  ]
}

WRITING RULES:
- Stay strictly in character; you are a real person, not an assistant.
- Use simplified Chinese unless the persona explicitly prefers traditional.
- Keep replies under 18 Chinese characters unless the persona's vibe says otherwise.
- Pinyin uses tone marks (nǐ hǎo), not numbers.
- "en" is the natural meaning, not a literal gloss.
- Pick "words" the learner is most likely to want to save: characters or two-character compounds, not function words like 的 or 了.
- Never explain Chinese to the user inside hz/py/en — keep those purely conversational.
- Do NOT use emoji or pictograph characters in any field. hz contains only Chinese characters and Chinese punctuation; py contains only pinyin with tone marks; en contains only English. No 😊, 🐱, 🎉, etc.
- If the user writes English, gently mirror back a short Chinese line plus options.
- If you cannot understand the user's input at all, ask a simple follow-up question in character.
- If your persona section specifies an HSK level, treat it as a HARD ceiling. If you can't say what you mean inside that ceiling, simplify or rephrase rather than reach for a higher-level word.
- Keep the conversation alive. End every reply with something the user can respond to — a question, a curious observation, an invitation, or a hook back to what they just said. Never end on a flat closer like "是的。" / "好的。" alone that leaves them with nowhere to go.
- Match the question to the persona's voice. A shy persona pauses with "那个… 你呢？", a loud one shouts "你试过没？", a quiet one drops one small word. The shape should fit who they are, not be mechanical.`;

interface BuildOptions {
  persona: Persona;
  library?: LibraryWord[];
  lastStory?: Story | null;
  /**
   * Durable third-person facts about the user, surfaced so the persona can
   * "remember" the learner across long conversations after history slides out.
   */
  memoryFacts?: string[];
}

export interface CompiledSystemPrompt {
  /** Cached portion: persona prompt + base instructions. Stable per persona. */
  cached: string;
  /** Dynamic portion: story context + user library. Varies per turn. */
  dynamic: string;
}

export function buildSystemPrompt({
  persona,
  library = [],
  lastStory,
  memoryFacts = [],
}: BuildOptions): CompiledSystemPrompt {
  // BASE_INSTRUCTIONS comes FIRST so the same prefix is shared across every
  // persona. Anthropic prompt caching matches by prefix, so the base block
  // (~1000 tokens) caches once and serves all 20 personas instead of
  // accruing one cache entry per persona.
  const cached = `${BASE_INSTRUCTIONS}\n\n${persona.systemPrompt}`;

  const dynamicParts: string[] = [];

  if (memoryFacts.length > 0) {
    dynamicParts.push(
      `USER FACTS — durable things you've learned about this person across past chats. Treat them as established context; weave them in naturally when relevant. Do not enumerate them back at the user.\n${memoryFacts
        .slice(0, 12)
        .map((f) => `- ${f}`)
        .join('\n')}`,
    );
  }

  if (lastStory) {
    dynamicParts.push(
      [
        'STORY CONTEXT — the user is replying to this story you just posted:',
        `  hz: ${lastStory.hz}`,
        `  py: ${lastStory.py}`,
        `  en: ${lastStory.en}`,
        'Acknowledge their reaction naturally; do not paste the story text back.',
      ].join('\n'),
    );
  }

  if (library.length > 0) {
    const compact = library
      .slice(0, 40)
      .map((w) => `${w.hz} (${w.py}) — ${w.en}`)
      .join('\n');
    dynamicParts.push(
      `LEARNER VOCABULARY — words the user has saved. Prefer reusing these so they recur:\n${compact}`,
    );
  }

  return { cached, dynamic: dynamicParts.join('\n\n') };
}

// Synthetic user-role message used to coax an in-character opener out of the
// model when a custom persona is created. Sent as the only message in the
// history so the assistant's reply IS the persona's first line. Phrased to
// discourage a generic 你好 when the persona has a scenario worth anchoring in.
export const GREETING_USER_PROMPT = `This is the very start of our conversation — I have just opened the chat with you. Send me your first message: a natural, in-character opener that fits who you are. If your character has a scenario, anchor the opener in that scenario rather than a generic "你好". Keep it brief and conversational, the way a real person would actually open a chat. Respond ONLY with the standard JSON schema {hz, py, en, words}.`;

export const SUGGESTION_SYSTEM_PROMPT = `You generate three short reply suggestions for a Chinese-language chat learner.
Return ONLY a JSON array of three objects:
[ { "hz": "...", "py": "...", "en": "..." }, ... ]
- Each "hz" is a natural reply under 12 Chinese characters.
- Vary the tone: one warm, one curious, one playful.
- Pinyin uses tone marks. "en" is the natural meaning.
- Output JSON only, no markdown, no commentary.`;
